<script setup>
import { reactive, ref } from "vue";

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  edit: {
    type: Object,
    required: true,
  },
  apiUrl: {
    type: String,
    required: true,
  },
  user: {
    type: String,
    required: true,
  },
});

let saveState = reactive({
  storyTrail: [],
  variables: {},
  story: "",
  card: "",
  seenUuids: [],
});

// UUIDs that are shown for the first time on the current page
let currentShowOnceVisible = ref([]);

let thisPage = {
  paragraphs: [],
  links: [],
};

let page = "home";

// Save slot management
let saveSlot = ref(1);
let showSaveMenu = ref(false);
let showLoadMenu = ref(false);

// ====================

let showPageData = reactive({ data: {} });

// ==================

// Save/Load Functions
function saveGame(key, data) {
  try {
    const jsonString = JSON.stringify(data);
    localStorage.setItem(key, jsonString);
    return true;
  } catch (error) {
    console.error("Save failed:", error);
    return false;
  }
}

function loadGame(key) {
  try {
    const jsonString = localStorage.getItem(key);
    return jsonString ? JSON.parse(jsonString) : null;
  } catch (error) {
    console.error("Load failed:", error);
    return null;
  }
}

function saveToSlot(slotNumber, gameData) {
  const key = `saveSlot_${slotNumber}`;
  return saveGame(key, {
    data: gameData,
    timestamp: new Date().toISOString(),
  });
}

function loadFromSlot(slotNumber) {
  const key = `saveSlot_${slotNumber}`;
  return loadGame(key);
}

function listAllSaves() {
  return Object.keys(localStorage)
    .filter((key) => key.startsWith("saveSlot_"))
    .map((key) => ({
      slot: key.replace("saveSlot_", ""),
      ...loadGame(key),
    }));
}

function createSaveSlots() {
  saveToSlot(1, {});
  saveToSlot(2, {});
  saveToSlot(3, {});
}

function resetSaveData() {
  saveState.storyTrail = [];
  saveState.variables = JSON.parse(JSON.stringify(props.data.variableDefs));
  saveState.story = "main";
  saveState.card = "start";
  saveState.seenUuids = [];
  currentShowOnceVisible.value = [];
}

// Initialize save slots if needed
if (listAllSaves().length < 3) {
  createSaveSlots();
}

// Save/Load actions
function startNewGame(slot) {
  saveSlot.value = slot;
  resetSaveData();
  saveToSlot(saveSlot.value, saveState);
  showSaveMenu.value = false;
  showLoadMenu.value = false;
  linkTo("main", "start");
}

function loadGameFromSlot(slot) {
  saveSlot.value = slot;
  const save = loadFromSlot(slot);
  if (save && save.data && save.data.story) {
    Object.assign(saveState, save.data);
    if (!saveState.seenUuids) saveState.seenUuids = [];
    currentShowOnceVisible.value = [];
    showLoadMenu.value = false;
    linkTo(saveState.story, saveState.card);
  }
}

function formatTimestamp(timestamp) {
  if (!timestamp) return "(empty)";
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function startGame() {
  console.log("Start game");
  saveState.variables = JSON.parse(JSON.stringify(props.data.variableDefs));
  linkTo("main", "start");
}

function getCardNode(story, card) {
  return props.data.stories[story].cards[card];
}

function showPage(story, card) {
  console.log("showing page", story, card);
  const cardData = props.data.stories[story].cards[card];
  const newlyVisible = [];

  const hasShowOnce = (conditions) =>
    conditions?.some((c) => c.variable === "__show_once__");

  if (cardData.paragraphs) {
    for (const paragraph of cardData.paragraphs) {
      if (
        hasShowOnce(paragraph.conditions) &&
        !saveState.seenUuids.includes(paragraph.text)
      ) {
        newlyVisible.push(paragraph.text);
      }
    }
  }
  if (cardData.links) {
    for (const link of cardData.links) {
      if (
        hasShowOnce(link.conditions) &&
        !saveState.seenUuids.includes(link.linkText)
      ) {
        newlyVisible.push(link.linkText);
      }
    }
  }

  currentShowOnceVisible.value = newlyVisible;
  saveState.seenUuids.push(...newlyVisible);

  showPageData.data = JSON.parse(JSON.stringify(cardData));
}

function linkTo(story, card) {
  console.log("linked: ", story, card);

  var thisCard = getCardNode(story, card);

  saveState.story = story;
  saveState.card = card;

  if (thisCard.nodeType == "start") {
    let goCard = props.data.stories[story].cards["start"].links[0].target;
    linkTo(story, goCard);
  }
  if (thisCard.nodeType == "end") {
    //let beforeStory = props.data.currentSaveState.storyBefore;
    //let beforeCard = props.data.currentSaveState.cardBefore;
    if (saveState.storyTrail.length == 0) return;

    let lastCrumb = saveState.storyTrail.pop();

    let linkCardId =
      props.data.stories[lastCrumb.story].cards[lastCrumb.card].links[0].target;
    linkTo(lastCrumb.story, linkCardId);
  }
  if (thisCard.nodeType == "card") {
    // Auto-save to current slot
    saveToSlot(saveSlot.value, saveState);
    showPage(story, card);
    //console.log("card ", story, card);
    //saveState.story = story;
    //this.currentCard = card;
    ///this.thisPage.paragraphs = JSON.parse(JSON.stringify(thisCard.paragraphs));
    ///this.thisPage.links = thisCard.links;
  }
  if (thisCard.nodeType == "detour") {
    let linkedStorylet = thisCard.detours[0].target;
    saveState.storyTrail.push({
      story: story,
      card: card,
    });
    linkTo(linkedStorylet, "start");
  }
  if (thisCard.nodeType == "logic") {
    console.log("logic");
    props.data.currentSaveState.story = story;
    //this.currentCard = card;
    let thisCard = props.data.stories[story].cards[card];
    let gotoLink = "";

    if (thisCard.links.length > 0) {
      gotoLink = thisCard.links[thisCard.links.length - 1].target;
    }

    if (thisCard.links.length > 1) {
      for (let i = 0; i < thisCard.links.length - 1; i++) {
        if (conditionCheck(thisCard.links[i].conditions, thisCard.links[i].conditionAndOr)) {
          gotoLink = thisCard.links[i].target;
          break;
        }
      }
    }
    linkTo(story, gotoLink);
    // currentStory;
  }
  if (thisCard.nodeType == "meta") {
    linkTo(story, thisCard.links[0].target);
    return;
  }
  if (thisCard.nodeType == "operator") {
    let thisOps = thisCard.links[0].operations;
    let linkedCard = thisCard.links[0].target;
    for (let i = 0; i < thisOps.length; i++) {
      if (thisOps[i].operator == "eq") {
        saveState.variables[thisOps[i].variable].value = thisOps[i].value;
      }
      if (thisOps[i].operator == "inc") {
        saveState.variables[thisOps[i].variable].value =
          1 * saveState.variables[thisOps[i].variable].value +
          1 * thisOps[i].value;
      }
      if (thisOps[i].operator == "dec") {
        saveState.variables[thisOps[i].variable].value =
          1 * saveState.variables[thisOps[i].variable].value -
          1 * thisOps[i].value;
      }
      if (thisOps[i].operator == "rnd") {
        let rndBounds = thisOps[i].value.split(",");
        let rndVal = getRndInteger(1 * rndBounds[0], 1 * rndBounds[1]);
        saveState.variables[thisOps[i].variable].value = rndVal;
      }
      if (thisOps[i].operator == "eval") {
        saveState.variables[thisOps[i].variable].value = eval(
          replaceVarsForEval(thisOps[i].value)
        );
      }
    }
    linkTo(story, linkedCard);
  }
}

function replaceVars(text) {
  console.log(text);

  console.log(saveState.variables);

  let matches = text.match(/(\[(.*?)\])/gi);
  if (matches && matches.length > 0) {
    for (let j = 0; j < matches.length; j++) {
      matches[j] = matches[j].replace("[", "");
      matches[j] = matches[j].replace("]", "");
      if (saveState.variables[matches[j]])
        text = text.replace(
          "[" + matches[j] + "]",
          saveState.variables[matches[j]].value
        );
    }
  }
  return text;
}

function replaceVarsForEval(text) {
  let matches = text.match(/(\[(.*?)\])/gi);
  let thisValue;
  if (matches && matches.length > 0) {
    for (let j = 0; j < matches.length; j++) {
      matches[j] = matches[j].replace("[", "");
      matches[j] = matches[j].replace("]", "");
      if (saveState.variables[matches[j]])
        thisValue = saveState.variables[matches[j]].value;
      if (isNaN(thisValue * 1)) {
        thisValue = "'" + thisValue + "'";
      }
      text = text.replace("[" + matches[j] + "]", thisValue);
    }
  }
  return text;
}

function conditionCheck(conditions, mode = "and", uuid = null) {
  if (conditions.length == 0) {
    return true;
  }
  let truthyCount = 0;
  for (let i = 0; i < conditions.length; i++) {
    if (conditions[i].variable === "__show_once__") {
      if (uuid && currentShowOnceVisible.value.includes(uuid)) {
        truthyCount++;
      }
      continue;
    }
    if (conditions[i].operator == "eq") {
      if (
        saveState.variables[conditions[i].variable].value == conditions[i].value
      ) {
        truthyCount++;
      }
    }
    if (conditions[i].operator == "neq") {
      if (
        saveState.variables[conditions[i].variable].value != conditions[i].value
      ) {
        truthyCount++;
      }
    }
    if (conditions[i].operator == "gt") {
      if (
        saveState.variables[conditions[i].variable].value * 1 >
        conditions[i].value * 1
      ) {
        truthyCount++;
      }
    }
    if (conditions[i].operator == "gte") {
      console.log(
        "gte",
        saveState.variables[conditions[i].variable].value,
        conditions[i].value
      );
      if (
        saveState.variables[conditions[i].variable].value * 1 >=
        conditions[i].value * 1
      ) {
        console.log("gte yes");
        truthyCount++;
      }
    }
    if (conditions[i].operator == "lt") {
      if (
        saveState.variables[conditions[i].variable].value * 1 <
        conditions[i].value * 1
      ) {
        truthyCount++;
      }
    }
    if (conditions[i].operator == "lte") {
      if (
        saveState.variables[conditions[i].variable].value * 1 <=
        conditions[i].value * 1
      ) {
        truthyCount++;
      }
    }
    if (conditions[i].operator == "eval") {
      if (eval(replaceVarsForEval(conditions[i].value))) {
        truthyCount++;
      }
    }
  }
  if (mode === "or") return truthyCount > 0;
  return truthyCount === conditions.length;
}

function getRndInteger(min, max) {
  return Math.floor(Math.random() * (max - min)) + min;
}

// =================
</script>

<template>
  <div class="basic-window" v-if="data">
    <div class="ui-container" v-if="data">
      <div class="win-header">
        <h3>Preview</h3>
      </div>

      <div v-if="props.data.stories" class="panel-buttons">
        <div class="expanded-button">
          <button class="ui-button ui-button-blue ui-button-medium button-repad" @click="startGame()">
            Start
          </button>
        </div>
        <div class="expanded-button hide">
          <button class="ui-button ui-button-green ui-button-medium button-repad" @click="showSaveMenu = !showSaveMenu">
            New Game
          </button>
        </div>
        <div class="expanded-button hide">
          <button class="ui-button ui-button-purple ui-button-medium button-repad"
            @click="showLoadMenu = !showLoadMenu">
            Load
          </button>
        </div>
      </div>

      <!-- Save/New Game Menu -->
      <div v-if="showSaveMenu" class="save-menu">
        <h4>Choose a save slot to start new game</h4>
        <div v-for="save in listAllSaves()" :key="save.slot" class="save-slot"
          @click="startNewGame(parseInt(save.slot))">
          <div class="slot-label">Save Slot {{ save.slot }}</div>
          <div class="slot-info">{{ formatTimestamp(save.timestamp) }}</div>
        </div>
      </div>

      <!-- Load Menu -->
      <div v-if="showLoadMenu" class="save-menu">
        <h4>Choose a saved game to load</h4>
        <div v-for="save in listAllSaves()" :key="save.slot" class="save-slot"
          :class="{ disabled: !save.data || !save.data.story }"
          @click="save.data && save.data.story && loadGameFromSlot(parseInt(save.slot))">
          <div class="slot-label">Save Slot {{ save.slot }}</div>
          <div class="slot-info">{{ formatTimestamp(save.timestamp) }}</div>
        </div>
      </div>

      <div class="win-scrollpane" id="preview-window">
        <div class="game-body" v-if="showPageData.data.paragraphs">
          <div v-for="paragraph in showPageData.data.paragraphs" v-bind:key="paragraph">
            <p v-if="conditionCheck(paragraph.conditions, paragraph.conditionAndOr, paragraph.text)">
              {{
                replaceVars(
                  props.data.loc[paragraph.text][props.data.currentLanguage]
                )
              }}
            </p>
          </div>
        </div>
        <div class="game-links">
          <div v-for="link in showPageData.data.links" v-bind:key="link">
            <div class="game-link" @click="linkTo(saveState.story, link.target)"
              v-if="conditionCheck(link.conditions, link.conditionAndOr, link.linkText) && link.linkText != 'na'">
              {{ props.data.loc[link.linkText][props.data.currentLanguage] }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.save-menu {
  background: rgba(0, 0, 0, 0.05);
  padding: 15px;
  margin: 10px 0;
  border-radius: 4px;
}

.save-menu h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
}

.save-slot {
  background: white;
  padding: 12px;
  margin: 8px 0;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.save-slot:hover {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.1);
}

.save-slot.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-slot.disabled:hover {
  border-color: transparent;
  background: white;
}

.slot-label {
  font-weight: bold;
  margin-bottom: 4px;
}

.slot-info {
  font-size: 12px;
  color: #666;
}
</style>
